'use client';

import { IT_INT } from '@slims/shared/constants';
import * as React from 'react';
import {
	Area,
	Bar,
	CartesianGrid,
	ComposedChart,
	Line,
	ReferenceArea,
	ReferenceDot,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

import { cn } from '../../lib/cn';

export type SlChartKind = 'line' | 'area' | 'bar';

export interface SlChartSeries {
	/** Nome mostrato in legenda e nel tooltip. */
	name: string;
	/** Colore della serie: un token (`var(--color-accent)`) o un hex esplicito. */
	color: string;
	/** Un valore per punto. Tutte le serie devono avere la stessa lunghezza. */
	vals: number[];
	/** Forma della serie. Default: `line`. */
	kind?: SlChartKind;
	/** Spessore del tratto, per line/area. Default: 2.4. */
	width?: number;
}

export interface SlChartProps {
	/** Serie sovrapposte, disegnate nell'ordine dato. */
	series: SlChartSeries[];
	/**
	 * Etichette dell'asse X, una per punto. La stringa vuota lascia il punto
	 * senza etichetta — è così che il mock dirada le ore (`['06','','08',…]`).
	 */
	x: string[];
	/** Etichetta estesa nel tooltip. Default: l'etichetta dell'asse. */
	hoverX?: (index: number) => string;
	/** Unità stampata in alto a sinistra. Default: `kWh`. */
	unit?: string;
	/** Altezza dell'area di disegno in px. Default: 300, massimo 520. Ignorato se `fillHeight`. */
	height?: number;
	/**
	 * Il grafico riempie tutta l'altezza disponibile invece di fermarsi a
	 * `height` — per due card affiancate in una `CardGrid` dove il resto del
	 * contenuto (schede, banner, footnote) ha lunghezza diversa da una
	 * all'altra: inseguire un numero in px fisso si disallinea appena il
	 * testo della card gemella va a capo diversamente (larghezza viewport,
	 * lunghezza dati reali). Richiede un antenato flex con altezza risolta
	 * (`PanelCard fill`).
	 */
	fillHeight?: boolean;
	/** Indice da marcare come picco sulla prima serie. */
	peak?: number;
	/**
	 * Indice da cui in poi i valori sono una stima: da lì parte la banda
	 * «stima AI», con la linea tratteggiata di confine.
	 */
	forecastFrom?: number;
	/** Mostra la legenda sopra il grafico. Default: true. */
	legend?: boolean;
	/** Testo mostrato quando non c'è nessun punto. */
	emptyMessage?: string;
	/** Descrizione per screen reader del grafico nel suo insieme. */
	ariaLabel?: string;
	className?: string;
}

/**
 * Scala «gradevole» del massimo: porta il fondo scala al prossimo valore tondo
 * (1, 1.5, 2, 3, 5, 7.5, 10 × potenza di dieci) invece che al massimo esatto,
 * così le quattro tacche cadono su numeri leggibili.
 */
function niceMax(max: number): number {
	if (max <= 0) return 1;
	const pow = 10 ** Math.floor(Math.log10(max));
	const n = max / pow;
	const step =
		n <= 1 ? 1 : n <= 1.5 ? 1.5 : n <= 2 ? 2 : n <= 3 ? 3 : n <= 5 ? 5 : n <= 7.5 ? 7.5 : 10;
	return step * pow;
}

/**
 * Quanti decimali servono a distinguere i tick del range [0, max]. Non un
 * numero fisso: un grafico con `max` sotto 1 (un impianto piccolo in MW,
 * es. 0.15 MW di picco) con 1 decimale fisso mostrava i tick "0", "0.1",
 * "0.1", "0.1", "0.2" — tre tacche indistinguibili sulla stessa etichetta.
 */
function decimalsForMax(max: number): number {
	if (max >= 10) return 0;
	if (max >= 1) return 1;
	if (max >= 0.1) return 2;
	if (max >= 0.01) return 3;
	return 4;
}

/**
 * Formato dei numeri su assi e tooltip: migliaia separate all'italiana,
 * `decimals` calcolato dalla scala del grafico (vedi `decimalsForMax`), non
 * dal singolo valore — altrimenti i tick di uno stesso asse avrebbero
 * precisioni diverse tra loro.
 */
function fmt(v: number, decimals: number): string {
	if (Math.abs(v) >= 1000) return Math.round(v).toLocaleString('it-IT', IT_INT);
	return v.toLocaleString('it-IT', {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
}

/** Chiave dati della serie i-esima. L'asse X usa l'indice, non l'etichetta:
 *  le etichette si ripetono (o sono vuote) e non identificherebbero il punto. */
const seriesKey = (i: number) => `s${i}`;

interface TooltipPayloadItem {
	dataKey?: string | number;
	value?: number;
}

const SlChart = ({
	series,
	x,
	hoverX,
	unit = 'kWh',
	height = 300,
	fillHeight = false,
	peak,
	forecastFrom,
	legend = true,
	emptyMessage = 'Nessun dato disponibile',
	ariaLabel,
	className,
}: SlChartProps) => {
	const gradientId = React.useId();
	const points = series[0]?.vals.length ?? 0;
	// Senza campioni disegniamo comunque l'anteprima del grafico (assi, griglia,
	// legenda) invece di sostituirlo con un riquadro di solo testo — una griglia
	// piatta a zero con il messaggio sopra dice di più di un placeholder vuoto,
	// e nel mock il grafico non sparisce mai. `x` guida quanti punti "vuoti"
	// disegnare quando anche le etichette dell'asse mancano.
	const isEmpty = points === 0;
	const effectivePoints = isEmpty ? Math.max(x.length, 8) : points;

	const data = React.useMemo(() => {
		return Array.from({ length: effectivePoints }, (_, i) => {
			const row: Record<string, string | number> = { k: String(i) };
			series.forEach((s, si) => {
				row[seriesKey(si)] = isEmpty ? 0 : (s.vals[i] ?? 0);
			});
			return row;
		});
	}, [series, effectivePoints, isEmpty]);

	const { max, ticks, decimals } = React.useMemo(() => {
		let raw = 0;
		for (const s of series) for (const v of s.vals) if (v > raw) raw = v;
		// Il +8% è il margine che nel mock impedisce alla serie di toccare il
		// bordo superiore del riquadro.
		const m = niceMax(raw * 1.08);
		return {
			max: m,
			ticks: [0, 0.25, 0.5, 0.75, 1].map((f) => m * f),
			decimals: decimalsForMax(m),
		};
	}, [series]);

	const hasBar = series.some((s) => s.kind === 'bar');
	const label = (i: number) => x[i] ?? '';

	return (
		<div
			className={cn('font-mono', fillHeight && 'flex h-full flex-col', className)}
			aria-label={ariaLabel}
		>
			{legend && (
				<div className="mb-2 flex flex-wrap gap-3.5 text-[12px] text-chart-label">
					{series.map((s) => (
						<span key={s.name} className="inline-flex items-center gap-1.5">
							<span
								className="inline-block w-3.5 rounded-sm"
								style={{ background: s.color, height: s.kind === 'bar' ? 11 : 3 }}
							/>
							{s.name}
						</span>
					))}
				</div>
			)}
			<div className={cn('relative', fillHeight && 'min-h-0 flex-1')}>
				{/* L'unità sta in alto a sinistra, fuori dall'asse: nel mock è
				    un'etichetta del riquadro, non un suffisso ripetuto sui tick. */}
				<span className="absolute left-0 top-0 z-1 text-[11px] text-chart-label">{unit}</span>
				{isEmpty && (
					// Inset asimmetrico, non `inset-6` uguale su tutti i lati: a
					// sinistra e in basso il riquadro deve restare dentro i due assi
					// (l'asse Y è largo 52px, `width={52}` sotto, e l'asse X occupa
					// altri ~28px di tick sotto il grafico), non sopra di essi.
					<div className="absolute top-8 right-6 bottom-10 left-16 z-1 flex items-center justify-center text-center text-[13px] text-text-muted">
						<span className="max-w-full rounded-md bg-panel/85 px-3 py-1.5">{emptyMessage}</span>
					</div>
				)}
				<ResponsiveContainer width="100%" height={fillHeight ? '100%' : Math.min(520, height)}>
					<ComposedChart data={data} margin={{ top: 22, right: 16, bottom: 6, left: 0 }}>
						<defs>
							{series.map((s, si) =>
								s.kind === 'area' ? (
									<linearGradient
										key={s.name}
										id={`${gradientId}-${si}`}
										x1="0"
										y1="0"
										x2="0"
										y2="1"
									>
										<stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
										<stop offset="100%" stopColor={s.color} stopOpacity={0} />
									</linearGradient>
								) : null,
							)}
						</defs>
						<CartesianGrid
							vertical={false}
							stroke="color-mix(in srgb, var(--color-border) 35%, transparent)"
						/>
						<XAxis
							dataKey="k"
							interval={0}
							tickFormatter={(k: string) => label(Number(k))}
							tick={{ fontSize: 12, fill: 'var(--color-chart-label)' }}
							tickLine={false}
							axisLine={{ stroke: 'var(--color-chart-axis)', strokeWidth: 1.2 }}
						/>
						<YAxis
							domain={[0, max]}
							ticks={ticks}
							tickFormatter={(v: number) => fmt(v, decimals)}
							tick={{ fontSize: 12, fill: 'var(--color-chart-label)' }}
							tickLine={false}
							axisLine={{ stroke: 'var(--color-chart-axis)', strokeWidth: 1.2 }}
							width={52}
						/>
						{forecastFrom != null && data[forecastFrom] && (
							<ReferenceArea
								x1={String(forecastFrom)}
								x2={String(points - 1)}
								fill="var(--color-accent)"
								fillOpacity={0.06}
								ifOverflow="extendDomain"
								label={{
									value: 'stima AI ▶',
									position: 'insideTopLeft',
									fill: 'var(--color-accent)',
									fontSize: 11,
								}}
							/>
						)}
						{forecastFrom != null && data[forecastFrom] && (
							<ReferenceLine
								x={String(forecastFrom)}
								stroke="var(--color-chart-axis)"
								strokeDasharray="3 3"
							/>
						)}
						<Tooltip
							cursor={{ stroke: 'var(--color-chart-axis)', strokeDasharray: '3 3' }}
							content={({ active, payload, label: k }) => {
								if (!active || !payload?.length) return null;
								const i = Number(k);
								const byKey = new Map(
									(payload as unknown as readonly TooltipPayloadItem[]).map((p) => [
										String(p.dataKey),
										p.value,
									]),
								);
								return (
									<div className="rounded-md border border-chart-axis bg-panel px-2.5 py-2 text-[11.5px] text-text-primary shadow-lg">
										<div className="mb-0.5 font-extrabold">
											{hoverX ? hoverX(i) : label(i) || `#${i + 1}`}
										</div>
										{series.map((s, si) => (
											<div key={s.name} className="mt-0.5 flex items-center gap-1.5">
												<span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
												<span className="text-text-muted">{s.name}</span>
												<strong className="ml-auto">
													{fmt(byKey.get(seriesKey(si)) ?? 0, decimals)}
												</strong>
											</div>
										))}
									</div>
								);
							}}
						/>
						{series.map((s, si) => {
							const key = seriesKey(si);
							if (s.kind === 'bar') {
								return <Bar key={s.name} dataKey={key} name={s.name} fill={s.color} radius={2} />;
							}
							if (s.kind === 'area') {
								return (
									<Area
										key={s.name}
										type="linear"
										dataKey={key}
										name={s.name}
										stroke={s.color}
										strokeWidth={s.width ?? 2.4}
										fill={`url(#${gradientId}-${si})`}
										dot={false}
										activeDot={{
											r: 4,
											fill: 'var(--color-panel)',
											stroke: s.color,
											strokeWidth: 2,
										}}
									/>
								);
							}
							return (
								<Line
									key={s.name}
									type="linear"
									dataKey={key}
									name={s.name}
									stroke={s.color}
									strokeWidth={s.width ?? 2.4}
									dot={false}
									activeDot={{ r: 4, fill: 'var(--color-panel)', stroke: s.color, strokeWidth: 2 }}
								/>
							);
						})}
						{peak != null && series[0] && !hasBar && (
							<ReferenceDot
								x={String(peak)}
								y={series[0].vals[peak] ?? 0}
								r={4.5}
								fill={series[0].color}
								stroke="var(--color-panel)"
								strokeWidth={1.5}
							/>
						)}
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
};
SlChart.displayName = 'SlChart';

export { SlChart };
